import { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import {
  Drawer,
  DrawerContent,
  DrawerNavigation,
} from '@progress/kendo-react-layout';
import { FaTimesCircle } from 'react-icons/fa';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { Input } from '@progress/kendo-react-inputs';
import FormsGrid from '../../components/grids/FormsGrid';
import EditFormDetails from '../../components/FormComps/Forms/EditFormDetails';
import AddFormForm from '../../components/FormComps/Forms/AddFormForm';
import { getForms, Form, createForm, duplicateForm } from '../../data/formData';
import { canEditArea } from '../../helpers/areaAccess';

import '../Styles.css';

interface FormsProps {
  allowedRoutes?: string[];
}

const Forms = (props: FormsProps) => {
  // Forms.Manage or an administrator (see areaAccess.ts). The server enforces the same.
  const canEdit = canEditArea(props.allowedRoutes, 'Forms');
  const [formsData, setFormsData] = useState<Form[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState<number | null>(null);
  const [addFormVisible, setAddFormVisible] = useState(false);
  const { showToast } = useToast();

  const handleAddForm = () => {
    setAddFormVisible(true);
    setSelectedFormId(null);
    setExpanded(true);
  };

  // The form being copied, and what to call the copy. Null means the prompt is
  // closed.
  const [duplicateTarget, setDuplicateTarget] = useState<Form | null>(null);
  const [duplicateName, setDuplicateName] = useState('');
  const [duplicating, setDuplicating] = useState(false);

  const handleDuplicateForm = (form: Form) => {
    setDuplicateTarget(form);
    // Suggested rather than blank, so the usual case is one click.
    setDuplicateName(`${form.name} (copy)`);
  };

  const confirmDuplicate = async () => {
    if (!duplicateTarget) return;

    const name = duplicateName.trim();

    if (!name) {
      showToast('Give the copy a name', 'info');
      return;
    }

    try {
      setDuplicating(true);
      const copy = await duplicateForm(duplicateTarget.formId, name);
      setDuplicateTarget(null);
      handleFormUpdate();
      showToast(`Created "${name}"`, 'success');
      // Straight into the copy, since the point of making one is to change it.
      setSelectedFormId(copy.formId);
      setAddFormVisible(false);
      setExpanded(true);
    } catch (error: any) {
      console.error('Error duplicating form:', error);
      showToast(
        error.response?.data?.message || 'Unable to duplicate the form',
        'error'
      );
    } finally {
      setDuplicating(false);
    }
  };

  const handleEditForm = (form: Form) => {
    setSelectedFormId(form.formId);
    setAddFormVisible(false);
    setExpanded(true);
  };

  const handleExpanded = () => {
    setExpanded((prevState) => !prevState);
    if (expanded) {
      setSelectedFormId(null);
      setAddFormVisible(false);
    }
  };

  const handleFormUpdate = () => {
    // Refresh the forms list after updates
    const fetchData = async () => {
      try {
        const formsData = await getForms();
        setFormsData(formsData);
      } catch (error: any) {
        console.error('Error fetching forms: ' + error.message);
      }
    };
    fetchData();
  };

  const newFormHandler = async (dataItem: any) => {
    try {
      await createForm(dataItem);

      const fetchData = async () => {
        const formsData = await getForms();
        setFormsData(formsData);
      };
      fetchData();

      handleExpanded();
      showToast('Form successfully added', 'success');
    } catch (error: any) {
      handleExpanded();
      showToast('Failed to add form. Please try again.', 'error');
      console.error('Error adding form: ' + error.message);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const formsData = await getForms();
        console.log('Fetched forms data:', formsData);
        setFormsData(formsData);
      } catch (error: any) {
        console.error('Error fetching forms: ' + error.message);
        showToast('Error loading forms', 'error');
      }
    };

    fetchData();
  }, []);

  return (
    <div>
      <Drawer
        expanded={expanded}
        position={'end'}
        width={1000}
        mode='overlay'
        animation={{
          duration: 400,
        }}
        onOverlayClick={handleExpanded}
      >
        <DrawerContent>
          <DrawerNavigation>
            {addFormVisible && canEdit && (
              <>
                <div className='form__title__container'>
                  <h1 className='form__title'>Add Form</h1>
                  <FaTimesCircle
                    className='closeform'
                    onClick={handleExpanded}
                  />
                </div>
                <div className='form__wrapper'>
                  <AddFormForm onSubmit={newFormHandler} />
                </div>
              </>
            )}
            {selectedFormId && (
              <>
                <div className='form__title__container'>
                  <h1 className='form__title'>{canEdit ? 'Edit Form' : 'Form'}</h1>
                  <FaTimesCircle
                    className='closeform'
                    onClick={handleExpanded}
                  />
                </div>
                <div className='form__wrapper'>
                  <EditFormDetails
                    formId={selectedFormId}
                    canEdit={canEdit}
                    onUpdate={handleFormUpdate}
                    onClose={handleExpanded}
                  />
                </div>
              </>
            )}
          </DrawerNavigation>
        </DrawerContent>
      </Drawer>

      <FormsGrid 
        data={formsData}
        onAdd={canEdit ? handleAddForm : undefined}
        onEdit={handleEditForm}
        onDuplicate={canEdit ? handleDuplicateForm : undefined}
      />

      {canEdit && duplicateTarget && (
        <Dialog
          title="Duplicate form"
          onClose={() => setDuplicateTarget(null)}
          width={420}
        >
          <div style={{ padding: '0.5rem 0' }}>
            <p style={{ marginTop: 0 }}>
              Copies the sections, questions and their options from
              {' '}<strong>{duplicateTarget.name}</strong>. Responses are not copied.
            </p>
            <label
              style={{
                display: 'block',
                fontWeight: 600,
                fontSize: '13px',
                marginBottom: '0.4rem',
              }}
            >
              Name for the copy
            </label>
            <Input
              value={duplicateName}
              onChange={(e) => setDuplicateName(String(e.value ?? ''))}
              // Enter is the obvious way to accept a one field prompt.
              onKeyDown={(e: any) => {
                if (e.key === 'Enter' && !duplicating) confirmDuplicate();
              }}
              autoFocus
              maxLength={200}
              style={{ width: '100%' }}
            />
          </div>
          <DialogActionsBar>
            <Button onClick={() => setDuplicateTarget(null)} disabled={duplicating}>
              Cancel
            </Button>
            <Button
              themeColor="primary"
              onClick={confirmDuplicate}
              disabled={duplicating || !duplicateName.trim()}
            >
              {duplicating ? 'Duplicating…' : 'Duplicate'}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default Forms;
